<script setup lang="ts">
import { Head } from '@inertiajs/vue3';
import { Printer } from 'lucide-vue-next';
import { onBeforeUnmount } from 'vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import EditorCanvas from './partials/EditorCanvas.vue';
import './print.css';

// Documents print in light mode regardless of the app's current theme —
// same expectation as any other document (Word, a PDF, …). This page
// only ever opens in its own tab/window (see Show.vue's exportPdf()),
// so forcing it here can't affect the theme of the tab the user came
// from. Done synchronously during setup (before Vue's first paint) to
// avoid a dark→light flash.
const wasDark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

if (typeof document !== 'undefined') {
    document.documentElement.classList.remove('dark');
}

onBeforeUnmount(() => {
    if (wasDark && typeof document !== 'undefined') {
        document.documentElement.classList.add('dark');
    }
});

// Same prop shape as Editor/Show.vue — both pages are built from
// DocumentController::buildEditorProps().
defineProps<{
    project: { id: string; name: string; citation_style: string };
    document: {
        id: string;
        title: string;
        content: string | null;
        numbering_enabled: boolean;
        updated_at: string;
    };
    references: ReferenceItem[];
    assets: Array<{
        id: string;
        original_name: string;
        mime: string;
        size_bytes: number;
        caption?: string | null;
        alt?: string | null;
        src: string;
        created_at?: string;
    }>;
    extractionGrid: {
        id: string;
        rows_count: number;
        columns: Array<{ id: string; name: string; type: string }>;
    } | null;
}>();

function printPage() {
    window.print();
}
</script>

<template>
    <Head :title="`${document.title} — Aperçu d'impression`" />

    <div class="editor-print-page min-h-screen bg-muted/30">
        <!-- Non-printing toolbar -->
        <div
            class="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-6 py-3 print:hidden"
        >
            <div>
                <p class="text-sm font-semibold text-foreground">
                    {{ document.title }}
                </p>
                <p class="text-xs text-muted-foreground">
                    {{ project.name }} — aperçu d'impression / export PDF
                </p>
            </div>
            <button
                type="button"
                class="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                @click="printPage"
            >
                <Printer class="h-4 w-4" />
                Imprimer / Enregistrer en PDF
            </button>
        </div>

        <!-- Document content — reuses the exact same NodeView tree as the live editor -->
        <div
            class="editor-print-sheet mx-auto max-w-3xl bg-background px-16 py-16 shadow-sm print:shadow-none"
        >
            <h1 class="mb-8 text-3xl font-bold text-foreground">
                {{ document.title }}
            </h1>
            <EditorCanvas
                :editable="false"
                :initial-content="document.content ?? ''"
                :references="references"
                :citation-style="project.citation_style"
                :numbering-enabled="document.numbering_enabled"
                :project-id="project.id"
                :assets="assets"
                :extraction-grid="extractionGrid"
            />
        </div>
    </div>
</template>
